import { ChangeDetectionStrategy, Component, effect, input, linkedSignal, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { TextInputComponent } from '@examdojo/ui/input';
import { TranslocoModule } from '@jsverse/transloco';
import { TagColor } from '../../data/tag.model';
import { TagColorPickerComponent } from '../tag-color-picker/tag-color-picker.component';
import { TagEmojiPickerComponent } from '../tag-emoji-picker/tag-emoji-picker.component';

export interface TagEditorData {
  name: string;
  color: TagColor;
  emoji: string | null;
}

@Component({
  selector: 'dojo-tag-editor',
  template: `
    <div class="flex flex-col">
      <div class="z-3 bg-neutral-0 sticky top-0 flex w-full flex-col gap-4 border-b border-neutral-200 p-3">
        <dojo-text-input [formCtrl]="nameCtrl" [size]="'xs'" [label]="'examdojo.tags.label' | transloco">
          @if (nameCtrl.value) {
            <button dojoInputSuffix mat-icon-button type="button" (click)="nameCtrl.setValue('')">
              <dojo-icon icon="circle-xmark" faPrefix="fas" class="text-secondary" />
            </button>
          }
        </dojo-text-input>
        <!-- Color Picker -->
        <dojo-tag-color-picker [(selectedColor)]="color" />
      </div>

      <!-- Emoji Picker -->
      <dojo-tag-emoji-picker [(selectedEmoji)]="emoji" class="p-3" />

      <div class="bg-neutral-0 sticky bottom-0 flex items-center justify-between border-t border-neutral-200 p-3">
        @if (showDelete()) {
          <dojo-button size="xs" fill="outline" [customColor]="'red'" (click)="delete.emit()">
            {{ 'examdojo.tags.delete_tag' | transloco }}
          </dojo-button>
        } @else {
          <div></div>
        }
        <dojo-button size="xs" [disabled]="!name()" (click)="onSave()">
          {{ 'examdojo.tags.save' | transloco }}
        </dojo-button>
      </div>
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    ButtonComponent,
    IconComponent,
    TextInputComponent,
    TagColorPickerComponent,
    TagEmojiPickerComponent,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagEditorComponent {
  constructor() {
    effect(() => {
      const nameValue = this.name();
      if (this.nameCtrl.value !== nameValue) {
        this.nameCtrl.setValue(nameValue, { emitEvent: false });
      }
    });

    this.nameCtrl.valueChanges
      .pipe(
        tap((value: string) => {
          this.name.set(value);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  readonly prefill = input<Partial<TagEditorData>>();
  readonly showDelete = input<boolean>(false);

  readonly save = output<TagEditorData>();
  readonly delete = output<void>();

  readonly name = linkedSignal(() => this.prefill()?.name ?? '');
  readonly color = linkedSignal<TagColor>(() => this.prefill()?.color ?? 'amber');
  readonly emoji = linkedSignal<string | null>(() => this.prefill()?.emoji ?? null);

  readonly nameCtrl = new FormControl<string>('', { nonNullable: true });

  onSave(): void {
    if (!this.name()) {
      return;
    }

    this.save.emit({
      name: this.name(),
      color: this.color(),
      emoji: this.emoji(),
    });
  }
}
